import { Download, OpenInNew } from '@mui/icons-material';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { isProblemDetailTextOr } from 'src/common/ProblemDetail';

import StepperActions from './components/StepperActions';
import { admiTenderService } from './admi-tender.service';
import { useTenderIdFromUrl } from './hooks';
import { DocumentType } from './dto/documents.dto';

export default function OfferEvaluationStepCreateDocuments() {
    const { t } = useTranslation();
    const [tenderId] = useTenderIdFromUrl();

    const [loading, setLoading] = useState<Partial<Record<DocumentType, boolean>>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [documentUrls, setDocumentUrls] = useState<Partial<Record<DocumentType, string>>>({});

    const createdMessages: Record<DocumentType, string> = {
        'cover-letter': t('Cover letter created.'),
        'price-comparison-table': t('Price comparison table created.'),
    };

    const handleGenerateDocument = async (documentType: DocumentType) => {
        if (!tenderId) {
            return;
        }

        setLoading((prev) => ({ ...prev, [documentType]: true }));
        setErrorMessage(null);
        try {
            const result = await admiTenderService.generateDocument(tenderId, documentType);
            setDocumentUrls((prev) => ({ ...prev, [documentType]: result.documentUrl }));
            // Generation takes far longer than the ~5 s a click lets a page open a tab, so the browser
            // blocks this unless pop-ups are allowed for the site; the link in the success alert always works
            window.open(result.documentUrl, '_blank');
        } catch (err) {
            // The interceptor rejects with the bare ProblemDetail body, so its detail is the backend's reason
            setErrorMessage(isProblemDetailTextOr(err, t('Failed to generate the document.')));
        } finally {
            setLoading((prev) => ({ ...prev, [documentType]: false }));
        }
    };

    const DownloadDocumentButton = ({ documentType, label }: { documentType: DocumentType; label: string }) => (
        <Button
            startIcon={<Download />}
            variant="contained"
            onClick={() => handleGenerateDocument(documentType)}
            loading={loading[documentType]}
        >
            {label}
        </Button>
    );

    return (
        <>
            <Box sx={{ mt: 3 }}>
                <Typography variant="h5" gutterBottom>
                    {t('Create Documents')}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <DownloadDocumentButton documentType="cover-letter" label={t('Generate cover letter')} />
                    <DownloadDocumentButton
                        documentType="price-comparison-table"
                        label={t('Generate price comparison table')}
                    />
                </Stack>

                {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrorMessage(null)}>
                        {errorMessage}
                    </Alert>
                )}

                {(Object.entries(documentUrls) as [DocumentType, string][]).map(([documentType, url]) => (
                    <Alert
                        key={documentType}
                        severity="success"
                        sx={{ mt: 2 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                startIcon={<OpenInNew />}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('Open document')}
                            </Button>
                        }
                    >
                        {createdMessages[documentType]}
                    </Alert>
                ))}

                <Alert severity="info" sx={{ mt: 2 }}>
                    {t(
                        'If the document is not opened in a new tab, please enable pop-ups for this site. In Chrome, you can do this by clicking the icon in the address bar.',
                    )}
                </Alert>
            </Box>

            <StepperActions />
        </>
    );
}
